import React, { Suspense } from "react"
import { lazily } from "react-lazily"

import classNames from "classnames"

import { formatNumber } from "shared/lib/priceUtils"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import type { DonutChartProps } from "../donut-chart/DonutChart"
import { DonutChartLoader } from "../donut-chart/ui/DonutChartLoader"
import "./styles/_kpi-card.scss"

const { DonutChart } = lazily(() => import("../donut-chart/DonutChart"))

function formatValue(value: string | number): string {
  return typeof value === "number"
    ? formatNumber({ number: value }).toString()
    : value
}

const Root = React.memo(({ children }: { children: React.ReactNode }) => {
  return <div className="card card--elevated kpi-card">{children}</div>
})

const Title = React.memo(({ title }: { title: string }) => {
  return <h2 className="kpi-card__title">{title}</h2>
})

const DonutChartComponent = React.memo(
  ({ data, centerText }: DonutChartProps) => {
    return (
      <Suspense fallback={<DonutChartLoader />}>
        <DonutChart data={data} centerText={centerText} />
      </Suspense>
    )
  }
)

const DataList = React.memo(
  ({
    data
  }: {
    data: {
      label: string
      value: string | number | undefined
    }[]
  }) => {
    return (
      <ul className="kpi-data-list">
        {data.map(({ label, value }) => (
          <li className="kpi-data-item" key={label}>
            <div className="kpi-data-item__label">{label}</div>
            {value === undefined ? (
              <div className="kpi-data-item__value kpi-data-item__value--loading">
                <Skeleton />
              </div>
            ) : (
              <div className="kpi-data-item__value">{formatValue(value)}</div>
            )}
          </li>
        ))}
      </ul>
    )
  }
)

const DataValue = React.memo(
  ({ value }: { value: string | number | undefined }) => {
    return (
      <div
        className={classNames("kpi-data-value", {
          "kpi-data-value--loading": value === undefined
        })}
      >
        {value === undefined ? <Skeleton /> : formatValue(value)}
      </div>
    )
  }
)

const KpiCard = {
  Root,
  Title,
  DonutChart: DonutChartComponent,
  DataList,
  DataValue
}

export { KpiCard }
